import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, Activity, Package, Search, LogOut, Upload, Target,
  FileText, Megaphone, ChevronDown, Truck, Settings, Link as LinkIcon, Star, Bookmark,
  Factory, ShoppingCart, PhoneCall, FileCode, Inbox, Mailbox, Smartphone, Monitor, Menu as MenuIcon, X, CalendarDays, Sparkles, Store, MailCheck, Gift, ShoppingBag, ClipboardCheck, ClipboardList,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Icon registry for dynamic menu rendering
const ICONS = {
  LayoutDashboard, Building2, Users, Activity, Package, Target, Truck, FileText, Megaphone,
  Link: LinkIcon, Star, Bookmark, Settings, Factory, ShoppingCart, PhoneCall, FileCode, Inbox, Mailbox, CalendarDays, Sparkles, Store, MailCheck, Gift, ShoppingBag, ClipboardCheck,
};

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/organisaties", label: "Organisaties", icon: Building2, id: "nav-organisaties" },
  { to: "/contactpersonen", label: "Contactpersonen", icon: Users, id: "nav-contactpersonen" },
  { to: "/activiteiten", label: "Activiteiten", icon: Activity, id: "nav-activiteiten" },
  { to: "/producten", label: "Producten", icon: Package, id: "nav-producten" },
  { to: "/voorraadaudit", label: "Voorraadaudit", icon: ClipboardList, id: "nav-voorraadaudit" },
  { to: "/strategie", label: "Strategie", icon: Sparkles, id: "nav-strategie" },
  { to: "/verkoopkansen", label: "Verkoopkansen", icon: Target, id: "nav-verkoopkansen" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, id: "nav-agenda" },
  { to: "/pakbonnen", label: "Pakbonnen", icon: Truck, id: "nav-pakbonnen" },
  { to: "/verkoopfacturen", label: "Verkoopfacturen", icon: FileText, id: "nav-verkoopfacturen" },
];

function useMenu() {
  const [items, setItems] = useState(null);
  const reload = async () => {
    try {
      const { data } = await api.get("/menu");
      setItems(data.items || []);
    } catch {
      setItems([]);
    }
  };
  useEffect(() => {
    reload();
    const onUpd = () => reload();
    window.addEventListener("menu-updated", onUpd);
    return () => window.removeEventListener("menu-updated", onUpd);
  }, []);
  return items;
}

function useShopOpenCount() {
  const [count, setCount] = useState(0);
  const reload = async () => {
    try {
      const { data } = await api.get("/shop/accounts/aantal-open");
      setCount(data?.aantal || 0);
    } catch { setCount(0); }
  };
  useEffect(() => {
    reload();
    const onUpd = () => reload();
    window.addEventListener("webshop-updated", onUpd);
    const iv = setInterval(reload, 60000);
    return () => { window.removeEventListener("webshop-updated", onUpd); clearInterval(iv); };
  }, []);
  return count;
}

function useInboxOngelezen() {
  const [count, setCount] = useState(0);
  const reload = async () => {
    try {
      const { data } = await api.get("/inbox/ongelezen-aantal");
      setCount(data?.aantal || 0);
    } catch { setCount(0); }
  };
  useEffect(() => {
    reload();
    const onUpd = () => reload();
    window.addEventListener("inbox-updated", onUpd);
    const iv = setInterval(reload, 60000);
    return () => { window.removeEventListener("inbox-updated", onUpd); clearInterval(iv); };
  }, []);
  return count;
}

function CampagnesNav({ onNavigate }) {
  const loc = useLocation();
  const isOnCamp = loc.pathname.startsWith("/campagnes") || loc.pathname.startsWith("/bellen");
  const [open, setOpen] = useState(isOnCamp);
  useEffect(() => { if (isOnCamp) setOpen(true); /* expand wanneer route actief */ }, [isOnCamp]);
  return (
    <div>
      <button onClick={() => setOpen(o => !o)} data-testid="nav-campagnes-toggle"
        className={`nav-item w-full ${isOnCamp ? "active" : ""}`}>
        <Megaphone className="h-4 w-4" />
        <span className="flex-1 text-left">Campagnes</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="ml-7 mt-1 space-y-0.5 border-l border-border pl-3">
          <NavLink to="/campagnes?tab=overzicht" onClick={onNavigate} data-testid="nav-camp-overzicht"
            className={() => `nav-item text-xs ${(loc.pathname === "/campagnes" && !loc.search.includes("nieuw")) ? "active" : ""}`}>
            Overzicht
          </NavLink>
          <NavLink to="/campagnes?tab=nieuw" onClick={onNavigate} data-testid="nav-camp-nieuw"
            className={() => `nav-item text-xs ${loc.search.includes("nieuw") ? "active" : ""}`}>
            Nieuwe campagne
          </NavLink>
          <NavLink to="/campagnes/segmenten" onClick={onNavigate} data-testid="nav-camp-segmenten"
            className={() => `nav-item text-xs ${loc.pathname.startsWith("/campagnes/segmenten") ? "active" : ""}`}>
            Segmenten
          </NavLink>
          <NavLink to="/agenda" onClick={onNavigate} data-testid="nav-camp-bellijst"
            className={() => `nav-item text-xs ${loc.pathname.startsWith("/agenda") || loc.pathname.startsWith("/bellen") ? "active" : ""}`}>
            Bellen (agenda)
          </NavLink>
          <NavLink to="/campagnes/ai" onClick={onNavigate} data-testid="nav-camp-ai"
            className={() => `nav-item text-xs ${loc.pathname.startsWith("/campagnes/ai") ? "active" : ""}`}>
            AI-campagne
          </NavLink>
          <NavLink to="/campagnes/webshop-uitnodigingen" onClick={onNavigate} data-testid="nav-camp-webshop-uitnodigingen"
            className={() => `nav-item text-xs ${loc.pathname.startsWith("/campagnes/webshop-uitnodigingen") ? "active" : ""}`}>
            Webshop-uitnodigingen
          </NavLink>
        </div>
      )}
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState({ organisaties: [], contactpersonen: [], producten: [] });
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setRes({ organisaties: [], contactpersonen: [], producten: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setRes(data); setOpen(true);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  const total = res.organisaties.length + res.contactpersonen.length + res.producten.length;

  return (
    <div className="relative w-full max-w-xl" ref={ref}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        data-testid="global-search-input"
        placeholder="Zoek organisaties, contacten, producten…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q && setOpen(true)}
        className="pl-9 h-10 bg-card"
      />
      {open && total > 0 && (
        <div data-testid="global-search-results" className="absolute z-50 mt-1 w-full bg-card border border-border rounded-lg shadow-lg overflow-hidden">
          {res.organisaties.length > 0 && (
            <div>
              <div className="px-3 pt-3 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">Organisaties</div>
              {res.organisaties.map((o) => (
                <button key={o.id} onClick={() => go(`/organisaties/${o.id}`)} className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex justify-between">
                  <span className="font-medium">{o.naam}</span>
                  <span className="text-muted-foreground">{o.plaats}</span>
                </button>
              ))}
            </div>
          )}
          {res.contactpersonen.length > 0 && (
            <div className="border-t border-border">
              <div className="px-3 pt-3 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">Contactpersonen</div>
              {res.contactpersonen.map((c) => (
                <button key={c.id} onClick={() => go(`/organisaties/${c.organisatie_id}`)} className="w-full text-left px-3 py-2 hover:bg-secondary text-sm">
                  {c.voornaam} {c.achternaam} <span className="text-muted-foreground">— {c.functie}</span>
                </button>
              ))}
            </div>
          )}
          {res.producten.length > 0 && (
            <div className="border-t border-border">
              <div className="px-3 pt-3 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">Producten</div>
              {res.producten.map((p) => (
                <button key={p.id} onClick={() => go(`/producten/${p.id}`)} className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex justify-between">
                  <span>{p.naam}</span>
                  <span className="text-muted-foreground">{p.sku}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InstellingenNav({ isAdmin, onNavigate }) {
  const loc = useLocation();
  const isOn = loc.pathname.startsWith("/instellingen");
  const [open, setOpen] = useState(isOn);
  useEffect(() => { if (isOn) setOpen(true); }, [isOn]);
  const sub = [
    { to: "/instellingen/bedrijf", label: "Bedrijfsgegevens", id: "bedrijf" },
    { to: "/instellingen/email", label: "E-mail & verzenden", id: "email" },
    { to: "/instellingen/verzending", label: "Verzenden", id: "verzending" },
    { to: "/instellingen/voorraad", label: "Voorraad", id: "voorraad" },
    ...(isAdmin ? [{ to: "/instellingen/webshop-categorieen", label: "Webshop-categorieën", id: "webshop-categorieen" }] : []),
    ...(isAdmin ? [{ to: "/instellingen/moneybird", label: "Moneybird", id: "moneybird" }] : []),
    ...(isAdmin ? [{ to: "/instellingen/channeldock", label: "ChannelDock", id: "channeldock" }] : []),
    ...(isAdmin ? [{ to: "/instellingen/claude", label: "Claude-connector", id: "claude" }] : []),
    { to: "/instellingen/api-koppelingen", label: "API's & koppelingen", id: "api-koppelingen" },
    { to: "/instellingen/menu", label: "Menu aanpassen", id: "menu" },
    { to: "/instellingen/presentaties", label: "Presentaties", id: "presentaties" },
    { to: "/instellingen/kenmerken", label: "Kenmerken", id: "kenmerken" },
    { to: "/instellingen/campagnes", label: "Campagnes", id: "campagnes" },
    { to: "/instellingen/email-ontwerpen", label: "E-mailontwerpen", id: "email-ontwerpen" },
    { to: "/instellingen/templates", label: "Templates (documenten)", id: "templates" },
    { to: "/instellingen/account", label: "Mijn account", id: "account" },
    { to: "/instellingen/gearchiveerd", label: "Gearchiveerde relaties", id: "gearchiveerd" },
    ...(isAdmin ? [{ to: "/instellingen/gebruikers", label: "Gebruikers", id: "gebruikers" }] : []),
    ...(isAdmin ? [{ to: "/instellingen/onderhoud", label: "Onderhoud", id: "onderhoud" }] : []),
  ];
  return (
    <div>
      <button onClick={() => setOpen(o => !o)} data-testid="nav-instellingen-toggle"
        className={`nav-item w-full ${isOn ? "active" : ""}`}>
        <Settings className="h-4 w-4" />
        <span className="flex-1 text-left">Instellingen</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="ml-7 mt-1 space-y-0.5 border-l border-border pl-3">
          {sub.map(it => (
            <NavLink key={it.id} to={it.to} onClick={onNavigate} data-testid={`nav-instellingen-${it.id}`}
              className={({ isActive }) => `nav-item text-xs ${isActive ? "active" : ""}`}>
              {it.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// Generiek uitklapbaar submenu (door gebruiker samengesteld)
function MenuGroup({ item, kinderen, onNavigate, renderLeaf }) {
  const loc = useLocation();
  const isActive = kinderen.some((c) => c.to !== "#" && loc.pathname.startsWith((c.to || "").split("?")[0]));
  const [open, setOpen] = useState(isActive);
  useEffect(() => { if (isActive) setOpen(true); }, [isActive]);
  const Icon = ICONS[item.icon] || LinkIcon;
  if (!kinderen.length) return null;
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} data-testid={`nav-group-${item.id}-toggle`}
        className={`nav-item w-full ${isActive ? "active" : ""}`}>
        <Icon className="h-4 w-4" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="ml-7 mt-1 space-y-0.5 border-l border-border pl-3">
          {kinderen.map((c) => renderLeaf(c, true))}
        </div>
      )}
    </div>
  );
}

// Gedeelde navigatie-inhoud (sidebar + mobiele drawer)
function NavContent({ user, onNavigate }) {
  const menuItems = useMenu();
  const shopOpen = useShopOpenCount();
  const inboxUnread = useInboxOngelezen();
  const items = menuItems ?? NAV.map(n => ({
    id: n.id.replace("nav-", ""), label: n.label, to: n.to, icon: n.icon.displayName || n.icon.name, visible: true, custom: false,
  }));
  const visibleItems = items.filter(i => i.visible !== false && i.id !== "templates"); // Templates staat onder Instellingen

  const renderLeaf = (it, compact = false) => {
    const Icon = ICONS[it.icon] || LinkIcon;
    const isExternal = /^https?:\/\//i.test(it.to);
    const cls = compact ? "nav-item text-xs" : "nav-item";
    const badgeCount = (it.to === "/webshop-aanvragen" && shopOpen > 0) ? shopOpen
      : (it.to === "/inbox" && inboxUnread > 0) ? inboxUnread : 0;
    const badge = badgeCount > 0 ? (
      <span data-testid={`nav-badge-${it.id}`}
        className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-accent text-accent-foreground text-[11px] font-semibold">
        {badgeCount}
      </span>
    ) : null;
    if (isExternal) {
      return (
        <a key={it.id} href={it.to} target="_blank" rel="noopener noreferrer" onClick={onNavigate}
           data-testid={`nav-${it.id}`} className={cls}>
          {!compact && <Icon className="h-4 w-4" />}
          {it.label}{badge}
        </a>
      );
    }
    return (
      <NavLink key={it.id} to={it.to} onClick={onNavigate} data-testid={`nav-${it.id}`}
        className={({ isActive }) => `${cls} ${isActive ? "active" : ""}`}
        end={it.to === "/dashboard"}>
        {!compact && <Icon className="h-4 w-4" />}
        {it.label}{badge}
      </NavLink>
    );
  };

  const groupIds = new Set(visibleItems.filter((i) => i.is_group).map((i) => i.id));
  const topLevel = visibleItems.filter((i) => !i.parent || !groupIds.has(i.parent));
  const kinderenVan = (gid) => visibleItems.filter((i) => i.parent === gid && !i.is_group);

  return (
    <>
      {topLevel.map((it) => {
        if (it.id === "campagnes" && !it.custom) return <CampagnesNav key={it.id} onNavigate={onNavigate} />;
        if (it.is_group) return <MenuGroup key={it.id} item={it} kinderen={kinderenVan(it.id)} onNavigate={onNavigate} renderLeaf={renderLeaf} />;
        return renderLeaf(it);
      })}
      {user?.rol === "admin" && (
        <>
          <div className="pt-4 mt-2 border-t border-border" />
          <NavLink to="/import" onClick={onNavigate} data-testid="nav-import"
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <Upload className="h-4 w-4" />
            Import / Migratie
          </NavLink>
        </>
      )}
      <div className="pt-4 mt-2 border-t border-border" />
      <InstellingenNav isAdmin={user?.rol === "admin"} onNavigate={onNavigate} />
    </>
  );
}

function ViewSwitch({ mode, setMode }) {
  return (
    <div className="flex items-center gap-1 rounded-lg bg-secondary p-1" data-testid="view-mode-switch">
      <button
        data-testid="view-mode-desktop"
        onClick={() => setMode("desktop")}
        className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${mode === "desktop" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
        <Monitor className="h-3.5 w-3.5" /> Desktop
      </button>
      <button
        data-testid="view-mode-ios"
        onClick={() => setMode("ios")}
        className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${mode === "ios" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
        <Smartphone className="h-3.5 w-3.5" /> iOS
      </button>
    </div>
  );
}

function ProfileBlock({ user, onLogout, mode, setMode }) {
  return (
    <div className="border-t border-border p-4 space-y-3">
      <div>
        <div className="text-sm font-medium" data-testid="current-user-name">{user?.naam}</div>
        <div className="text-xs text-muted-foreground">{user?.email}</div>
      </div>
      <ViewSwitch mode={mode} setMode={setMode} />
      <Button
        variant="ghost"
        size="sm"
        data-testid="logout-btn"
        onClick={onLogout}
        className="w-full justify-start"
      >
        <LogOut className="h-4 w-4 mr-2" /> Uitloggen
      </Button>
    </div>
  );
}

// Mobiele app-shell (gerenderd binnen de iframe of op een echt mobiel scherm)
function MobileShell({ user, onLogout, mode, setMode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <div className="min-h-screen flex flex-col bg-background overflow-x-hidden" data-testid="ios-shell">
      <header className="h-14 shrink-0 border-b border-border flex items-center px-4 gap-3 sticky top-0 z-30">
        <button data-testid="mobile-menu-btn" onClick={() => setDrawerOpen(true)} className="p-1.5 -ml-1.5 rounded-lg hover:bg-black/5">
          <MenuIcon className="h-5 w-5" />
        </button>
        <img src="/nextproduct-wordmark.png?v=2" alt="NextProduct" className="h-6 w-auto object-contain" />
      </header>

      <div className="px-4 py-3 border-b border-border bg-background">
        <GlobalSearch />
      </div>

      <main className="flex-1 px-4 py-5 overflow-y-auto" data-testid="ios-main">
        <Outlet />
      </main>

      {drawerOpen && (
        <div className="fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 bg-card border-r border-border flex flex-col animate-in slide-in-from-left duration-200">
            <div className="px-5 py-5 border-b border-border flex items-center justify-between">
              <div>
                <img src="/nextproduct-wordmark.png?v=2" alt="NextProduct" className="h-6 w-auto object-contain" />
                <div className="text-xs text-foreground/60 mt-1">B2B relatiebeheer</div>
              </div>
              <button data-testid="mobile-drawer-close" onClick={() => setDrawerOpen(false)} className="p-1.5 rounded-lg hover:bg-black/5">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              <NavContent user={user} onNavigate={() => setDrawerOpen(false)} />
            </nav>
            <ProfileBlock user={user} onLogout={onLogout} mode={mode} setMode={setMode} />
          </div>
        </div>
      )}
    </div>
  );
}

// Vult op mobiel elke tabelcel van een `data-label` (uit de kolomkop) zodat de
// CSS in index.css er een leesbare kaart-rij van maakt. App-breed, zonder
// per-tabel aanpassingen; desktop blijft ongewijzigd. Opt-out met class "no-cards".
function useMobileTableCards() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const relabel = () => {
      document.querySelectorAll("table:not(.no-cards)").forEach((t) => {
        const heads = t.querySelectorAll("thead tr:last-child th");
        if (!heads.length) return;
        const labels = Array.from(heads).map((th) => (th.textContent || "").trim());
        t.querySelectorAll("tbody tr").forEach((tr) => {
          let ci = 0;
          Array.from(tr.children).forEach((cell) => {
            if (cell.tagName !== "TD") return;
            const span = cell.colSpan || 1;
            if (span === 1 && labels[ci]) cell.setAttribute("data-label", labels[ci]);
            ci += span;
          });
        });
      });
    };
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(relabel); };
    schedule();
    const obs = new MutationObserver(schedule);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => { cancelAnimationFrame(raf); obs.disconnect(); };
  }, []);
}

export default function Layout() {
  const { user, logout } = useAuth();
  useMobileTableCards();
  const navigate = useNavigate();
  const [mode, setModeRaw] = useState(() => localStorage.getItem("crm-view-mode") || "desktop");
  // true wanneer dit document binnen de iPhone-iframe draait
  const isFramed = typeof window !== "undefined" && window.self !== window.top;
  // Detecteer echt mobiel viewport (iPhone/Android in de browser)
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const h = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", h) : mq.addListener(h);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", h) : mq.removeListener(h); };
  }, []);

  const setMode = (m) => {
    setModeRaw(m);
    localStorage.setItem("crm-view-mode", m);
    // signaleer het bovenliggende venster (storage-event vuurt enkel cross-frame)
    try { window.dispatchEvent(new StorageEvent("storage", { key: "crm-view-mode", newValue: m })); } catch {}
  };
  const onLogout = () => { logout(); navigate("/login"); };

  // Houd het bovenliggende venster in sync wanneer de iframe de modus wijzigt
  useEffect(() => {
    const h = (e) => { if (e.key === "crm-view-mode") setModeRaw(e.newValue || "desktop"); };
    window.addEventListener("storage", h);
    return () => window.removeEventListener("storage", h);
  }, []);

  // Binnen de iframe of op een echt mobiel viewport: altijd de mobiele app-shell tonen
  if (isFramed || (isMobile && mode !== "ios")) {
    return <MobileShell user={user} onLogout={onLogout} mode={mode} setMode={setMode} />;
  }

  // Bovenliggend venster in iOS-modus: render de webapp op iPhone 17 Pro-breedte (402pt), schermvullend in hoogte
  if (mode === "ios") {
    return (
      <div className="h-screen w-full flex flex-col items-center bg-neutral-900" data-testid="ios-viewport">
        <div className="w-full max-w-[402px] flex-1 min-h-0 bg-background border-x border-neutral-800 overflow-hidden">
          <iframe
            title="iOS webapp"
            data-testid="ios-iframe"
            src={window.location.href}
            className="w-full h-full border-0 block"
          />
        </div>
        <button
          data-testid="ios-exit-btn"
          onClick={() => setMode("desktop")}
          className="shrink-0 py-2 flex items-center gap-2 text-xs font-medium text-neutral-300 hover:text-white transition-colors"
        >
          <Monitor className="h-3.5 w-3.5" /> Terug naar desktop
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 border-r border-border bg-card sticky top-0 h-screen flex flex-col">
        <div className="px-5 py-6 border-b border-border">
          <img src="/nextproduct-wordmark.png?v=2" alt="NextProduct" className="h-7 w-auto max-w-full object-contain" />
          <div className="text-xs text-foreground/60 mt-1.5">B2B relatiebeheer</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <NavContent user={user} />
        </nav>
        <ProfileBlock user={user} onLogout={onLogout} mode={mode} setMode={setMode} />
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur sticky top-0 z-40 flex items-center px-8 min-[1920px]:px-12 gap-4">
          <GlobalSearch />
          <div className="ml-auto text-xs text-muted-foreground hidden md:block">
            {user?.rol === "admin" ? "Admin" : "Gebruiker"}
          </div>
        </header>
        <main className="flex-1 px-8 py-8 min-[1920px]:px-12 min-[1920px]:py-10 max-w-[1400px] min-[1920px]:max-w-[1760px] min-[2400px]:max-w-[2200px] min-[2800px]:max-w-[2560px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
